import React, { useEffect, useMemo } from 'react';
import { useParams, Link, Navigate, useLocation } from 'react-router-dom';
import { Calendar, ArrowLeft, ArrowRight, User, Home, Info, BookOpen, Tag, Hash } from 'lucide-react';
import { getPostBySlug, getAllPosts } from './blogPosts';
import { getLocalizedSlug, getEnglishSlugSync, getLocalizedSlugSync } from './slugMapping';
import { SEOHead } from '../components/SEOHead';
import { useLocaleContext } from '../context/LocaleContext';
import { generateBlogPostingSchema, injectArticleSchema } from '../utils/articleSchema';
import { generateHowToSchema, injectHowToSchema, isHowToPost } from '../utils/howToSchema';
import { generateBlogFAQSchema, injectBlogFAQSchema, hasFAQContent } from '../utils/blogFaqSchema';
import { BlogImage, getImageAspectRatio } from '../components/BlogImage';
import MarkdownPreviewOptimized from '../components/MarkdownPreviewOptimized';
import { getCategoryById, toTagSlug } from './categories';
import { RelatedKnowledge } from '../components/RelatedKnowledge';
import { buildCategoryUrl } from './categorySlugMapping';
import { injectSyntaxHighlightingCSS } from '../utils/dynamicCss';
import { TableOfContents } from '../components/TableOfContents';

export const BlogPost: React.FC = () => {
  const { slug: localizedSlug } = useParams<{ slug: string }>();
  const { locale } = useLocaleContext();
  const location = useLocation();

  // Slug mapping - use SYNCHRONOUS resolution for SSR compatibility
  // useEffect doesn't run during SSR, so async functions can't be used for initial state
  // getEnglishSlugSync uses statically bundled mappings that work synchronously
  const englishSlug = localizedSlug ? getEnglishSlugSync(localizedSlug, locale) : undefined;
  const [shouldRedirect, setShouldRedirect] = React.useState(false);
  const [redirectPath, setRedirectPath] = React.useState<string>('');

  // Check if we need to redirect from old English slug to localized slug
  // Note: Initial slug resolution is now synchronous (above), so this only handles redirects
  useEffect(() => {
    // Skip redirect check for English locale - no localized slugs
    if (!localizedSlug || locale === 'en') {
      return;
    }

    const checkRedirect = async () => {
      // For non-English locales, check if URL uses English slug instead of localized slug
      const correctLocalizedSlug = await getLocalizedSlug(englishSlug || localizedSlug, locale);

      // If the URL slug doesn't match the correct localized slug, redirect
      if (correctLocalizedSlug !== localizedSlug) {
        const localePrefix = locale === 'pt-BR' ? 'pt-br' : locale === 'ms-MY' ? 'my' : locale === 'es' ? 'es' : locale;
        const newPath = `/${localePrefix}/blog/${correctLocalizedSlug}`;
        setRedirectPath(newPath);
        setShouldRedirect(true);
      }
    };

    checkRedirect();
  }, [localizedSlug, locale, englishSlug]);

  const post = englishSlug ? getPostBySlug(englishSlug, locale) : undefined;

  // Get all posts for related articles
  const allPosts = useMemo(() => getAllPosts(locale), [locale]);

  // Helper function to get localized URL
  const getLocalizedUrl = (path: string): string => {
    if (locale === 'en') return path;
    const prefix = locale === 'pt-BR' ? 'pt-br' : locale === 'ms-MY' ? 'my' : locale === 'es' ? 'es' : locale;
    return `/${prefix}${path}`;
  };

  // Get related posts (2-4, excluding current post)
  // Prioritize posts from the same category, then other posts
  // Use slug-based seeding for consistent randomization
  const relatedPosts = useMemo(() => {
    if (!post) return [];

    // Filter out current post
    const otherPosts = allPosts.filter(p => p.slug !== post.slug);

    // Separate posts by category match
    const sameCategory = otherPosts.filter(p => p.category === post.category);
    const otherCategory = otherPosts.filter(p => p.category !== post.category);

    // Create a simple hash from the slug for consistent randomization
    const hash = post.slug.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);

    // Shuffle both arrays based on hash
    const shuffleWithHash = (posts: typeof otherPosts) => {
      return [...posts].sort((a, b) => {
        const aHash = a.slug.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const bHash = b.slug.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return ((aHash + hash) % 100) - ((bHash + hash) % 100);
      });
    };

    const shuffledSameCategory = shuffleWithHash(sameCategory);
    const shuffledOtherCategory = shuffleWithHash(otherCategory);

    // Determine count (2-4 posts based on hash)
    const count = 2 + (hash % 3); // 2, 3, or 4

    // Prioritize same category, then fill with other category
    const result = [
      ...shuffledSameCategory,
      ...shuffledOtherCategory,
    ].slice(0, count);

    return result;
  }, [post, allPosts]);

  // Helper function to get blog URL with localized slug
  // CLS FIX: Use synchronous slug lookup to prevent re-render after async load
  const getBlogUrl = (englishSlug: string): string => {
    const localizedSlug = getLocalizedSlugSync(englishSlug, locale);
    return getLocalizedUrl(`/blog/${localizedSlug}`);
  };

  // Determine if we should show "About Us" card (10% of posts)
  const showAboutCard = useMemo(() => {
    if (!post) return false;
    const hash = post.slug.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return hash % 10 === 0; // 10% chance
  }, [post]);

  // Helper function to format date based on locale
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const localeMap: Record<string, string> = {
      'en': 'en-US',
      'de': 'de-DE',
      'pt-BR': 'pt-BR',
      'ms-MY': 'ms-MY',
      'es': 'es-ES',
      'fr': 'fr-FR',
      'ja': 'ja-JP',
      'id': 'id-ID',
    };
    const browserLocale = localeMap[locale] || 'en-US';
    return date.toLocaleDateString(browserLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Get localized text
  const getText = (key: string): string => {
    const translations: Record<string, Record<string, string>> = {
      backToBlog: {
        en: 'Back to Blog',
        de: 'Zurück zum Blog',
        'pt-BR': 'Voltar ao Blog',
        'ms-MY': 'Kembali ke Blog',
        es: 'Volver al Blog',
        fr: 'Retour au Blog',
        ja: 'ブログに戻る',
        id: 'Kembali ke Blog',
      },
      backToAllArticles: {
        en: 'Back to all articles',
        de: 'Zurück zu allen Artikeln',
        'pt-BR': 'Voltar para todos os artigos',
        'ms-MY': 'Kembali ke semua artikel',
        es: 'Volver a todos los artículos',
        fr: 'Retour à tous les articles',
        ja: 'すべての記事に戻る',
        id: 'Kembali ke semua artikel',
      },
      backToHome: {
        en: 'Back to Home',
        de: 'Zurück zur Startseite',
        'pt-BR': 'Voltar ao Início',
        'ms-MY': 'Kembali ke Laman Utama',
        es: 'Volver al Inicio',
        fr: 'Retour à l\'accueil',
        ja: 'ホームに戻る',
        id: 'Kembali ke Beranda',
      },
      recommendedArticles: {
        en: 'Recommended Articles',
        de: 'Empfohlene Artikel',
        'pt-BR': 'Artigos Recomendados',
        'ms-MY': 'Artikel Disyorkan',
        es: 'Artículos Recomendados',
        fr: 'Articles recommandés',
        ja: 'おすすめの記事',
        id: 'Artikel yang Direkomendasikan',
      },
      readArticle: {
        en: 'Read article',
        de: 'Artikel lesen',
        'pt-BR': 'Ler artigo',
        'ms-MY': 'Baca artikel',
        es: 'Leer artículo',
        fr: 'Lire l\'article',
        ja: '記事を読む',
        id: 'Baca artikel',
      },
      ctaTitle: {
        en: 'Ready to Remove AI Watermarks?',
        de: 'Bereit, KI-Wasserzeichen zu entfernen?',
        'pt-BR': 'Pronto para Remover Marcas d\'Água de IA?',
        'ms-MY': 'Bersedia untuk Membuang Tanda Air AI?',
        es: '¿Listo para Eliminar Marcas de Agua de IA?',
        fr: 'Prêt à supprimer les filigranes IA ?',
        ja: 'AI透かしを削除する準備はできましたか？',
        id: 'Siap Menghapus Watermark AI?',
      },
      ctaDescription: {
        en: 'Try our free AI watermark removal tool. Detect and clean invisible characters from your text and documents in seconds.',
        de: 'Probieren Sie unser kostenloses KI-Wasserzeichen-Entfernungstool aus. Erkennen und bereinigen Sie unsichtbare Zeichen aus Ihrem Text und Dokumenten in Sekunden.',
        'pt-BR': 'Experimente nossa ferramenta gratuita de remoção de marcas d\'água de IA. Detecte e limpe caracteres invisíveis do seu texto e documentos em segundos.',
        'ms-MY': 'Cuba alat percuma kami untuk membuang tanda air AI. Kesan dan bersihkan aksara halimunan daripada teks dan dokumen anda dalam beberapa saat.',
        es: 'Prueba nuestra herramienta gratuita de eliminación de marcas de agua de IA. Detecta y limpia caracteres invisibles de tu texto y documentos en segundos.',
        fr: 'Essayez notre outil gratuit de suppression de filigrane IA. Détectez et nettoyez les caractères invisibles de vos textes et documents en quelques secondes.',
        ja: '無料のAI透かし削除ツールをお試しください。テキストや文書から見えない文字を数秒で検出・削除できます。',
        id: 'Coba alat penghapus watermark AI gratis kami. Deteksi dan bersihkan karakter tak terlihat dari teks dan dokumen Anda dalam hitungan detik.',
      },
      ctaButton: {
        en: 'Try GPT Watermark Remover',
        de: 'GPT-Wasserzeichen-Entferner testen',
        'pt-BR': 'Experimentar Removedor de Marca d\'Água GPT',
        'ms-MY': 'Cuba Penyingkir Tanda Air GPT',
        es: 'Probar Removedor de Marcas de Agua GPT',
        fr: 'Essayer le suppresseur de filigrane GPT',
        ja: 'GPT透かし削除ツールを試す',
        id: 'Coba Penghapus Watermark GPT',
      },
      aboutTitle: {
        en: 'Learn More About Us',
        de: 'Erfahren Sie mehr über uns',
        'pt-BR': 'Saiba Mais Sobre Nós',
        'ms-MY': 'Ketahui Lebih Lanjut Tentang Kami',
        es: 'Conoce Más Sobre Nosotros',
        fr: 'En savoir plus sur nous',
        ja: '私たちについてもっと知る',
        id: 'Pelajari Lebih Lanjut Tentang Kami',
      },
      aboutDescription: {
        en: 'Discover our mission to help users maintain privacy and control over AI-generated content. Learn about our technology and commitment to transparency.',
        de: 'Entdecken Sie unsere Mission, Nutzern zu helfen, Privatsphäre und Kontrolle über KI-generierte Inhalte zu wahren. Erfahren Sie mehr über unsere Technologie und unser Engagement für Transparenz.',
        'pt-BR': 'Descubra nossa missão de ajudar os usuários a manter a privacidade e o controle sobre o conteúdo gerado por IA. Saiba mais sobre nossa tecnologia e compromisso com a transparência.',
        'ms-MY': 'Temui misi kami untuk membantu pengguna mengekalkan privasi dan kawalan ke atas kandungan yang dijana AI. Ketahui tentang teknologi kami dan komitmen terhadap ketelusan.',
        es: 'Descubre nuestra misión de ayudar a los usuarios a mantener la privacidad y el control sobre el contenido generado por IA. Conoce nuestra tecnología y compromiso con la transparencia.',
        fr: 'Découvrez notre mission d\'aider les utilisateurs à préserver leur vie privée et leur contrôle sur le contenu généré par l\'IA. Apprenez-en plus sur notre technologie et notre engagement envers la transparence.',
        ja: 'AI生成コンテンツに対するプライバシーとコントロールを維持するユーザーを支援する私たちの使命をご覧ください。私たちの技術と透明性へのコミットメントについてお伝えします。',
        id: 'Temukan misi kami untuk membantu pengguna menjaga privasi dan kontrol atas konten yang dihasilkan AI. Pelajari tentang teknologi kami dan komitmen kami terhadap transparansi.',
      },
      aboutButton: {
        en: 'About Us',
        de: 'Über uns',
        'pt-BR': 'Sobre Nós',
        'ms-MY': 'Tentang Kami',
        es: 'Acerca de Nosotros',
        fr: 'À propos de nous',
        ja: '私たちについて',
        id: 'Tentang Kami',
      },
    };
    return translations[key]?.[locale] || translations[key]?.['en'] || '';
  };

  // Inject syntax highlighting CSS dynamically
  useEffect(() => {
    injectSyntaxHighlightingCSS().catch(console.error);
  }, []);

  // Scroll to top when navigating to a new blog post
  // Use requestAnimationFrame to prevent forced reflow
  useEffect(() => {
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, [localizedSlug]);

  useEffect(() => {
    // Guard against SSR - only run on client
    if (typeof window === 'undefined' || !post) return;

    // Generate BlogPosting Schema with correct localized URL
    const schema = generateBlogPostingSchema({
      title: post.title,
      description: post.description,
      image: post.coverImage,
      author: post.author,
      date: post.date,
      dateModified: post.date,
      keywords: post.keywords,
      category: post.category,
      tags: post.tags,
      locale: locale,
      currentPath: location.pathname, // Pass full pathname with locale prefix
    });

    // Inject BlogPosting schema and get cleanup function
    const cleanupArticle = injectArticleSchema(schema);

    // Check if this is a How-To post and inject HowTo schema
    let cleanupHowTo: (() => void) | undefined;
    if (isHowToPost(post)) {
      const howToSchema = generateHowToSchema(post, locale, location.pathname);
      if (howToSchema) {
        cleanupHowTo = injectHowToSchema(howToSchema);
      }
    }

    // Check if post has FAQ content and inject FAQ schema
    let cleanupFAQ: (() => void) | undefined;
    if (hasFAQContent(post)) {
      const canonicalUrl = `https://gpt-watermark-remover.com${location.pathname}`;
      const faqSchema = generateBlogFAQSchema(post, canonicalUrl);
      if (faqSchema) {
        cleanupFAQ = injectBlogFAQSchema(faqSchema);
      }
    }

    // Return combined cleanup function
    return () => {
      cleanupArticle();
      if (cleanupHowTo) cleanupHowTo();
      if (cleanupFAQ) cleanupFAQ();
    };
  }, [post, locale, location.pathname]);

  // Handle 301 redirect if needed (from old English slug to localized slug)
  if (shouldRedirect && redirectPath) {
    console.log(`[BlogPost] 301 Redirecting to: ${redirectPath}`);
    return <Navigate to={redirectPath} replace />;
  }

  // Note: Loading skeleton removed - slug resolution is now synchronous via getEnglishSlugSync
  // This ensures SSR renders actual content instead of a loading state

  if (!post) {
    // Don't use Navigate in SSR - it breaks rendering
    // Instead, show a "not found" message with a link
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h1 className="text-4xl font-bold mb-4">Blog Post Not Found</h1>
        <p className="text-neutral-600 dark:text-neutral-300 mb-8">
          The blog post you're looking for doesn't exist.
        </p>
        <Link
          to={getLocalizedUrl('/blog')}
          className="inline-flex items-center gap-2 px-6 py-3 bg-accent-600 text-white rounded-lg hover:bg-accent-700"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={post.title}
        description={post.description}
      />

      <article className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back to Blog Link */}
        <Link
          to={getLocalizedUrl('/blog')}
          className="inline-flex items-center gap-2 text-accent-600 dark:text-accent-400 hover:gap-3 transition-all mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          {getText('backToBlog')}
        </Link>

        {/* Post Header */}
        <header className="mb-8">
          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-600 dark:text-neutral-400 mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <time dateTime={post.date}>{formatDate(post.date)}</time>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{post.author}</span>
            </div>
          </div>

          {/* Category */}
          {post.category && (() => {
            const category = getCategoryById(post.category);
            if (category) {
              const categoryLabel = category.label[locale as keyof typeof category.label] || category.label.en;
              return (
                <Link
                  to={buildCategoryUrl(category.id, locale)}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-accent-600 hover:bg-accent-700 text-white text-sm font-medium rounded-full transition-colors mb-4"
                >
                  <Tag className="w-4 h-4" />
                  {categoryLabel}
                </Link>
              );
            }
            return null;
          })()}

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-charcoal-900 dark:text-white mb-4">
            {post.title}
          </h1>

          {/* Description */}
          <p className="text-xl text-neutral-600 dark:text-neutral-300 leading-relaxed">
            {post.description}
          </p>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {post.tags.map((tag, index) => (
                <Link
                  key={index}
                  to={getLocalizedUrl(`/blog/tag/${toTagSlug(tag)}`)}
                  className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium bg-neutral-100 dark:bg-charcoal-800 text-neutral-700 dark:text-neutral-300 rounded-full hover:bg-accent-100 dark:hover:bg-accent-900/30 hover:text-accent-700 dark:hover:text-accent-300 transition-colors"
                >
                  <Hash className="w-3.5 h-3.5" />
                  {tag}
                </Link>
              ))}
            </div>
          )}
        </header>

        {/* Divider */}
        <hr className="border-neutral-200 dark:border-charcoal-700 mb-8" />

        {/* Table of Contents - For SEO and navigation */}
        <TableOfContents content={post.content} locale={locale} />

        {/* Cover Image - LCP FIX: Moved below fold, lazy loaded, smaller size */}
        <div
          className="relative rounded-xl overflow-hidden mb-8 shadow-lg max-w-2xl mx-auto"
          style={{ aspectRatio: getImageAspectRatio(post.coverImage) }}
        >
          <BlogImage
            coverImage={post.coverImage}
            title={post.title}
            variant="card"
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Post Content - Using Optimized Markdown Renderer */}
        <div className="prose prose-lg dark:prose-invert max-w-none
          prose-headings:font-bold prose-headings:text-charcoal-900 dark:prose-headings:text-white
          prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
          prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
          prose-p:text-neutral-700 dark:prose-p:text-neutral-300 prose-p:leading-relaxed
          prose-a:text-accent-600 dark:prose-a:text-accent-400 prose-a:no-underline hover:prose-a:underline
          prose-strong:text-charcoal-900 dark:prose-strong:text-white
          prose-code:text-accent-600 dark:prose-code:text-accent-400 prose-code:bg-neutral-100 dark:prose-code:bg-charcoal-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
          prose-pre:bg-charcoal-900 dark:prose-pre:bg-charcoal-950 prose-pre:border prose-pre:border-charcoal-700
          prose-ul:list-disc prose-ol:list-decimal
          prose-li:text-neutral-700 dark:prose-li:text-neutral-300
          prose-blockquote:border-l-4 prose-blockquote:border-accent-500 prose-blockquote:bg-neutral-50 dark:prose-blockquote:bg-charcoal-800/50 prose-blockquote:py-2 prose-blockquote:px-4
          prose-img:rounded-lg prose-img:shadow-lg
        ">
          <MarkdownPreviewOptimized content={post.content} />
        </div>

        {/* Related Knowledge Articles */}
        {post && (
          <RelatedKnowledge
            blogPost={{
              slug: post.slug,
              title: post.title,
              description: post.description,
              keywords: post.keywords,
              tags: post.tags,
              category: post.category,
            }}
            locale={locale}
            maxItems={3}
          />
        )}

        {/* CTA Section */}
        <div className="mt-16 p-8 bg-gradient-to-br from-accent-600 to-accent-700 rounded-xl shadow-2xl text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {getText('ctaTitle')}
          </h2>
          <p className="text-white/90 text-lg mb-6 max-w-2xl mx-auto">
            {getText('ctaDescription')}
          </p>
          <Link
            to={getLocalizedUrl('/')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-accent-600 font-semibold rounded-lg hover:bg-neutral-100 transition-colors shadow-lg"
          >
            {getText('ctaButton')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Recommended Articles */}
        {relatedPosts.length > 0 && (
          <div className="mt-16">
            <div className="flex items-center gap-3 mb-8">
              <BookOpen className="w-6 h-6 text-accent-600 dark:text-accent-400" />
              <h2 className="text-2xl md:text-3xl font-bold text-charcoal-900 dark:text-white">
                {getText('recommendedArticles')}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.slug}
                  to={getBlogUrl(relatedPost.slug)}
                  className="group bg-white dark:bg-charcoal-800 rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 border border-neutral-200 dark:border-charcoal-700 hover:border-accent-500 dark:hover:border-accent-500 hover:-translate-y-1"
                >
                  {/* Cover Image */}
                  <div className="relative overflow-hidden aspect-video">
                    <BlogImage
                      coverImage={relatedPost.coverImage}
                      title={relatedPost.title}
                      variant="preview"
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>

                  {/* Card Content */}
                  <div className="p-6">
                    {/* Date */}
                    <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400 mb-3">
                      <Calendar className="w-4 h-4" />
                      <time dateTime={relatedPost.date}>{formatDate(relatedPost.date)}</time>
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-bold text-charcoal-900 dark:text-white mb-2 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4 line-clamp-2">
                      {relatedPost.description}
                    </p>

                    {/* Read More */}
                    <div className="inline-flex items-center gap-2 text-accent-600 dark:text-accent-300 font-semibold text-sm group-hover:gap-3 transition-all">
                      {getText('readArticle')}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}

              {/* About Us Card (10% of posts) */}
              {showAboutCard && (
                <Link
                  to={getLocalizedUrl('/about')}
                  className="group bg-gradient-to-br from-purple-600 to-purple-700 dark:from-purple-700 dark:to-purple-800 rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 border-2 border-purple-500 dark:border-purple-600 hover:-translate-y-1 p-6 flex flex-col justify-center"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-white/20 rounded-lg">
                      <Info className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      {getText('aboutTitle')}
                    </h3>
                  </div>

                  <p className="text-white/90 text-sm mb-4 leading-relaxed">
                    {getText('aboutDescription')}
                  </p>

                  <div className="inline-flex items-center gap-2 text-white font-semibold text-sm group-hover:gap-3 transition-all">
                    {getText('aboutButton')}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Back to Blog and Home */}
        <div className="mt-12 pt-8 border-t border-neutral-200 dark:border-charcoal-700 flex flex-wrap gap-4 justify-between items-center">
          <Link
            to={getLocalizedUrl('/blog')}
            className="inline-flex items-center gap-2 text-accent-600 dark:text-accent-400 hover:gap-3 transition-all group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            {getText('backToAllArticles')}
          </Link>

          <Link
            to={getLocalizedUrl('/')}
            className="inline-flex items-center gap-2 text-accent-600 dark:text-accent-400 hover:gap-3 transition-all group"
          >
            <Home className="w-4 h-4" />
            {getText('backToHome')}
          </Link>
        </div>
      </article>
    </>
  );
};

export default BlogPost;
